import axios from 'axios';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import {
  GET,
  POST,
  PUT,
  DELETE,
  AUTHENTICATION_ERROR,
  emailRoutes,
  PATCH,
} from 'constants/api';
import { REQUEST_TIMEOUT } from 'constants/durations';
import { getUserToken } from 'services/token';
import { createDomEvent } from 'services/common';
import { getBaseUrl, getMailerMicroserviceBaseUrl } from './env-service';

export const checkHttpMethod = method => {
  const allowedMethods = [GET, POST, PUT, DELETE, PATCH];
  if (!allowedMethods.includes(method)) {
    throw new Error(`The http method ${String(method)} is not allowed`);
  }
};

export const getAbsoluteUrl = (url, shouldUseMailerMicroservice) => {
  const BASE_URL = getBaseUrl();
  if (!url) return BASE_URL;

  const isAbsoluteUrl = /(?:[a-z]+:)?\/\//i;
  if (!shouldUseMailerMicroservice) {
    return isAbsoluteUrl.test(url) ? url : `${BASE_URL}${url}`;
  }
  return isAbsoluteUrl.test(url)
    ? url
    : `${getMailerMicroserviceBaseUrl()}${url}`;
};

const checkAuthenticationError = (url, response) => {
  const { error } = response || {};
  const { SEND_SAVED_TEMPLATE } = emailRoutes;
  if (
    error &&
    error.message &&
    error.message.indexOf('authorized to make') > -1 &&
    url !== SEND_SAVED_TEMPLATE
  ) {
    createDomEvent(AUTHENTICATION_ERROR);
  }
};

export const getDataFromResponse = url => response => {
  let updatedResponse = response;
  if (isString(response)) {
    try {
      updatedResponse = JSON.parse(response);
    } catch (error) {
      updatedResponse = response;
    }
  }
  checkAuthenticationError(url, updatedResponse);
  return updatedResponse;
};

export const validateResponseStatus = responseStatus => {
  return responseStatus >= 200 && responseStatus < 300;
};

export const getAuthorizationHeader = withoutAuthorization => {
  const token = getUserToken();
  return token && !withoutAuthorization ? `Bearer ${token}` : false;
};

const getDefaultOptions = shouldUseMailerMicroservice => ({
  url: shouldUseMailerMicroservice
    ? getMailerMicroserviceBaseUrl()
    : getBaseUrl(),
  method: POST,
  headers: { 'Content-Type': 'application/json' },
  query: {},
  withoutAuthorization: false,
  types: null,
});

function regularApi(options, shouldUseMailerMicroservice) {
  const { url, method, headers, data, withoutAuthorization, params } = merge(
    {},
    getDefaultOptions(shouldUseMailerMicroservice),
    options,
  );

  const authHeader = getAuthorizationHeader(withoutAuthorization);
  if (authHeader) headers.Authorization = authHeader;

  checkHttpMethod(method);

  const axiosOptions = {
    method,
    url: getAbsoluteUrl(url, shouldUseMailerMicroservice),
    headers,
    timeout: REQUEST_TIMEOUT,
    transformResponse: [getDataFromResponse(url)],
    responseType: options.isDownload ? 'blob' : 'json',
    validateStatus: validateResponseStatus,
    onUploadProgress: options.onUploadProgress,
  };
  if (data instanceof FormData) axiosOptions.data = data;
  else if (Array.isArray(data)) axiosOptions.data = data;
  else if (!isEmpty(data)) axiosOptions.data = data;

  if (!isEmpty(params)) axiosOptions.params = params;

  return axios(axiosOptions)
    .then(response => {
      return response.data;
    })
    .catch(e => {
      if (e.response) {
        // Override the error message with the one from the server
        if (e.response.data.error && e.response.data.error.message) {
          e.message = e.response.data.error.message;
        } else e.message = e.response.data.message;
        throw e;
      }
      throw new Error(e.message);
    });
}

export async function mailerServiceApi(options) {
  const res = await regularApi(options, true);
  if (res.success === true) return res.data;
  throw new Error(res?.message);
}

export const buildUrl = ({ url, query }) => {
  const encodedQuery = new URLSearchParams(query).toString();
  if (encodedQuery) return `${url}?${encodedQuery}`;
  return url;
};

export default regularApi;
